import Router from "next/router";

import { getLoginToken, getRetailShopId, getRetailShopIdWait, getShopId, removeLoginTokenAndShopId } from "./retailShop.core";
import { logReqError } from "./log.core";

//  @ts-ignore
async function getHeader(option: HeaderOption, additional: { [key: string]: string } = undefined) {
  var headers = {
    ...additional,
  };

  if (option.useToken && getLoginToken()) {
    //  @ts-ignore
    headers["x-access-token"] = getLoginToken();
  }

  if (option.useShopId && getShopId()) {
    //  @ts-ignore
    headers["shop-id"] = getShopId();
  }

  if (option.useRetailShopId) {
    const retailId = await getRetailShopIdWait();
    if (retailId) {
      headers["retailshop-id"] = retailId;
    }
  }

  if (option.useUserId) {
    headers["user-id"] = option.useUserId;
  }

  const locale = getHeaderLocale(option);
  headers["locale"] = locale;

  return headers;
}

function getHeaderLocale(option: HeaderOption) {
  if (option.locale) {
    return option.locale;
  } else if (Router.locale) {
    return Router.locale;
  } else {
    return "en";
  }
}

interface HeaderOption {
  useToken?: boolean;
  useShopId?: boolean;
  useRetailShopId?: boolean;
  useUserId?: string;
  locale?: string;
  query?: { [key: string]: string };
}

export async function get<T>(
  url: string,
  //  @ts-ignore
  option: HeaderOption = {},
  byPassErrorLogging = false
) {
  option = { locale: undefined, useRetailShopId: true, useShopId: true, useToken: true, ...option };
  const headers = { ...(await getHeader(option)) };

  const input = process.env.NEXT_PUBLIC_URL_ROOT + url;

  const init = {
    headers,
  };

  let res: Response;

  try {
    res = await fetch(input, init);
  } catch (err) {
    logReqError({ req: { init, input }, resData: err });
    throw err;
  }

  if (!byPassErrorLogging) {
    if (res.status !== 200) {
      const errorContent = await res.json();

      if (
        res.status === 401 &&
        errorContent.message &&
        (errorContent.message === "invalidToken" || errorContent.message === "tokenNotExists")
      ) {
        removeLoginTokenAndShopId();
      } else {
        if (res.status >= 300) {
          const isRetailNotFound = errorContent && errorContent.message === "retailNotfound";

          if (!isRetailNotFound) {
            logReqError({
              req: {
                init,
                input,
              },
              res,
              resData: errorContent,
            });
          }
        }
      }

      throw new Error(`Fetch Failed with url '${url}': ${JSON.stringify(errorContent)}`);
    }
  }

  return {
    data: (await res.json()) as T,
    status: res.status,
  };
}

export async function post<T>(
  url: string, //
  data: any,
  option: HeaderOption = {}
) {
  option = { useRetailShopId: true, useShopId: true, useToken: true, ...option };
  const input = process.env.NEXT_PUBLIC_URL_ROOT + url;

  const init = {
    body: JSON.stringify(data),
    headers: {
      "Content-Type": "application/json",
      locale: getHeaderLocale(option),
      "retailshop-id": option.useRetailShopId ? getRetailShopId() : undefined,
      "shop-id": option.useShopId ? getShopId() : undefined,
      "x-access-token": option.useToken ? getLoginToken() : undefined,
      "user-id": option.useUserId ? option.useUserId : undefined,
    },
    method: "POST",
  };

  let res: Response;

  try {
    //  @ts-ignore
    res = await fetch(input, init);
  } catch (err) {
    //  @ts-ignore
    logReqError({ req: { init, input }, resData: err });
    throw err;
  }

  if (res.status >= 300) {
    const data = await res.json();

    logReqError({
      req: {
        //  @ts-ignore
        init,
        input,
      },
      res,
      resData: data,
    });

    console.error(data);
    throw new Error(`Fetch Failed with url '${url}': ${JSON.stringify(data)}`);
  }

  return {
    data: (await res.json()) as T,
    status: res.status,
  };
}

export async function put<T>(
  url: string,
  data: any,
  option: HeaderOption = { useRetailShopId: true, useShopId: true, useToken: true }
) {
  const input = process.env.NEXT_PUBLIC_URL_ROOT + url;

  const init = {
    body: JSON.stringify(data),
    headers: {
      "Content-Type": "application/json",
      locale: getHeaderLocale(option),
      "retailshop-id": option.useRetailShopId ? getRetailShopId() : undefined,
      "shop-id": option.useShopId ? getShopId() : undefined,
      "x-access-token": option.useToken ? getLoginToken() : undefined,
    },
    method: "PUT",
  };

  let res: Response;

  try {
    //  @ts-ignore
    res = await fetch(input, init);
  } catch (err) {
    //  @ts-ignore
    logReqError({ req: { init, input }, resData: err });
    throw err;
  }

  if (res.status >= 300) {
    const data = await res.json();

    logReqError({
      req: {
        //  @ts-ignore
        init,
        input,
      },
      res,
      resData: data,
    });

    console.error(data);
    throw new Error(`Fetch Failed with url '${url}': ${JSON.stringify(data)}`);
  }

  return {
    data: (await res.json()) as T,
    status: res.status,
  };
}

export async function postDontHandleError<T>(
  url: string,
  data: any,
  option: HeaderOption = { useRetailShopId: true, useShopId: true, useToken: true }
) {
  const input = process.env.NEXT_PUBLIC_URL_ROOT + url;
  const init = {
    body: JSON.stringify(data),
    headers: {
      "Content-Type": "application/json",
      locale: getHeaderLocale(option),
      "retailshop-id": option.useRetailShopId ? getRetailShopId() : undefined,
      "shop-id": option.useShopId ? getShopId() : undefined,
      "x-access-token": option.useToken ? getLoginToken() : undefined,
    },
    method: "POST",
  };

  let res: Response;

  try {
    //  @ts-ignore
    res = await fetch(input, init);
  } catch (err) {
    //  @ts-ignore
    logReqError({ req: { init, input }, resData: err });
    throw err;
  }

  const resData = res.headers.get("content-length") === "0" ? undefined : ((await res.json()) as T);

  if (res.status >= 300) {
    logReqError({
      req: {
        //  @ts-ignore
        init,
        input,
      },
      res,
      resData,
    });
  }

  return {
    data: resData,
    status: res.status,
  };
}
